import { EmailEngineSevice } from "../../../../../../../server/api/email-engine-v2";
import {
  useApiLoader,
  type UseApiLoaderResponse,
} from "../../../../../../../server/api/hooks/use-api-loader";
import { useToken } from "../../../../../../../server/auth/use-token";
import type { UseSupabaseLoaderState } from "../../../../../../../server/supabase/hooks/types";
import { Waiting } from "../../../../../../../server/supabase/hooks/use-supabase-loader";
import { useEmailDesign } from "../../../../../../email-design/hooks/use-email-design";
import { useConstructedDesign } from "../../../../../../lexical-email-moment/hooks/use-constructed-design";
import type { SerialisedEditorDesign } from "../../../../../../message/editor/types/serialised-editor-design";
import type { SerialisedLexicalEditorDesign } from "../../../../../../message/editor/types/serialised-editor-design";
import { useMessageBodyCreator } from "../../../../../../message/editor/utils/create-message-body/create-message-body";
import type { MomentPreviewTags } from "../../../../../server/supabase-preview-service";
import type { UUID } from "../../../../../../../utils/uuid";
import type { PreviewMomentData } from "../types";
import type { MomentPreviewInfo } from "./use-preview-info";
import { usePreviewTags } from "./use-preview-tags";
import { extractSurveyIdFromDesign, useSurveyPreviewData } from "./use-survey-preview-data";

type Params = {
  email_design_id: UUID | null | undefined;
  account_id: UUID;
  poll_ids: UUID[] | null | undefined;
  form_design: SerialisedEditorDesign | null;
  dynamic_segment_id_map: PreviewMomentData["dynamic_segment_id_map"];
  preview_info: UseSupabaseLoaderState<MomentPreviewInfo | null>;
  preview_test_data: UseSupabaseLoaderState<MomentPreviewTags | null>;
  preview_design: SerialisedLexicalEditorDesign | null;
  subject: string | null;
  email_additional_merge_fields: string[];
  use_unlayer_email_preview: boolean;
  dynamic_condition_fields?: Record<string, boolean>;
  channel?: "work_email" | "whatsapp";
};

export function useBuiltMjmlMessage({
  email_design_id,
  account_id,
  poll_ids,
  form_design,
  dynamic_segment_id_map,
  preview_info,
  preview_test_data,
  preview_design,
  subject,
  email_additional_merge_fields,
  use_unlayer_email_preview,
  dynamic_condition_fields,
  channel = "work_email",
}: Params): UseApiLoaderResponse<string> {
  const getToken = useToken();

  const { loading: createMessageBodyLoading, data: createMessageBody } = useMessageBodyCreator();

  const { loading: loadingEmailDesign, data: emailDesign } = useEmailDesign(
    email_design_id ?? undefined,
  );

  const { loading: mergeFieldsLoading, data: mergeFields } = usePreviewTags({
    dynamic_segment_id_map,
    preview_info,
    preview_test_data,
    design: preview_design,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
    config: loadingEmailDesign
      ? { loading: true }
      : { loading: false, additional_merge_fields: emailDesign?.used_merge_fields ?? [] },
  });

  const {
    constructDesign: { loading: constructedDesignLoading, data: constructedDesign },
  } = useConstructedDesign(form_design, emailDesign);

  const surveyId = constructedDesignLoading
    ? null
    : extractSurveyIdFromDesign(constructedDesign?.design ?? form_design);
  const { loading: surveyLoading, data: surveyData } = useSurveyPreviewData({
    body: surveyId ? `{{#each survey_result_${surveyId}}}` : undefined,
  });

  return useApiLoader(async () => {
    if (
      mergeFieldsLoading ||
      createMessageBodyLoading ||
      constructedDesignLoading ||
      loadingEmailDesign ||
      surveyLoading
    )
      throw new Waiting();

    if (!createMessageBody) throw new Error("No message body creator");

    const token = await getToken();

    const { data: messageBodyData, errorMessage } = await createMessageBody({
      editorState: constructedDesign?.design ?? null,
      channel,
      format: "email",
      account_id,
      email_design_id: email_design_id ?? undefined,
      poll_ids: poll_ids || [],
    });

    if (errorMessage || !messageBodyData?.body)
      throw new Error(errorMessage ?? "Error creating message body");

    const body = `<mjml>${emailDesign?.styles_mjml ?? ""}
      <mj-body>
      ${emailDesign?.header_mjml ?? ""}
      ${messageBodyData.body}
      ${emailDesign?.footer_mjml ?? ""}
      </mj-body>
      </mjml>`;

    const service = new EmailEngineSevice(token);

    const contentMailMergeFields = Object.fromEntries(
      (mergeFields?.tags ?? []).map((field) => [field.key, field.value ?? ""]),
    );

    const surveyMergeFields = surveyData
      ? ({ [surveyData.key]: surveyData.value } as unknown as Record<string, string | null>)
      : {};

    const response = await service.buildMessage({
      body,
      mail_merge_fields: { person_id: "test", ...contentMailMergeFields, ...surveyMergeFields },
      dynamic_condition_fields:
        dynamic_condition_fields ?? mergeFields?.dynamic_condition_fields ?? {},
      mode: "mjml",
    });

    if (response.error || !response.data)
      throw new Error(response.error ?? "Error building message");

    const data = response.data.data;

    // Create a DOM parser to modify the HTML content before setting it in the iframe
    const parser = new DOMParser();
    const doc = parser.parseFromString(data, "text/html");

    // Find all links and update them to open in new tabs
    const links = doc.querySelectorAll("a");
    links.forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });

    // Convert back to string
    return doc.documentElement.outerHTML;
  }, [
    mergeFieldsLoading,
    createMessageBodyLoading,
    constructedDesignLoading,
    loadingEmailDesign,
    createMessageBody,
    getToken,
    constructedDesign?.design,
    account_id,
    email_design_id,
    poll_ids,
    emailDesign?.styles_mjml,
    emailDesign?.header_mjml,
    emailDesign?.footer_mjml,
    mergeFields?.tags,
    mergeFields?.dynamic_condition_fields,
    dynamic_condition_fields,
    channel,
    surveyLoading,
    surveyData,
  ]);
}
